import clsx from 'clsx';
import { forwardRef } from 'react';

type Props = {
  variant?: 'primary' | 'secondary' | 'cancel' | 'text' | 'tertiary';
  fullWidth?: boolean;
  rounded?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
};

interface ButtonProps
  extends React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>,
    Props {
  loading?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      children,
      className,
      loading,
      disabled,
      rounded,
      leftIcon,
      rightIcon,
      ...props
    },
    ref
  ) => {
    const baseStyles =
      'p-[12px] text-[14px] leading-[20px] relative font-medium capitalize flex justify-center items-center transition-all duration-300 gap-[10px] disabled:opacity-50 cursor-pointer';

    const variantStyles = clsx({
      'bg-transparent text-[#949EAC] border-none': variant === 'text',
      'bg-white text-primary-900 border border-gray-400 hover:border-primary-500 active:border-primary-300':
        variant === 'tertiary',
      'bg-white text-primary-900 border border-primary-500': variant === 'secondary',
      'bg-transparent text-[#949EAC] border-0': variant === 'cancel',
      'bg-primary-500 text-white': variant === 'primary', // Default variant
    });

    const roundedStyles = rounded ? 'rounded-[100px]' : 'rounded-[8px]';

    return (
      <button
        ref={ref}
        className={clsx(
          baseStyles,
          variantStyles,
          roundedStyles,
          {
            'opacity-50 pointer-events-none': loading,
          },
          className
        )}
        disabled={disabled}
        {...props}
      >
        {leftIcon && <span className="absolute left-4">{leftIcon}</span>}
        {loading ? 'Loading...' : children}
        {rightIcon && <span className="absolute right-4">{rightIcon}</span>}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
